import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  type OnDestroy,
  output,
} from '@angular/core';
import { type ControlValueAccessor, NgControl } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faMinus } from '@fortawesome/pro-regular-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'rcp-input-checkbox',
  template: `
    <div
      class="checkbox-container"
      [class.checked]="checked()"
      [class.indeterminate]="controlIndeterminate()"
      (click)="onClick($event)"
      [class.disabled]="controlDisabled() || readOnly()"
      [class.positive]="positive()"
      role="checkbox"
      [attr.aria-checked]="checked()"
    >
      @if (checked()) {
        <div class="check">
          <fa-icon [icon]="faCheck" />
        </div>
      } @else if (controlIndeterminate()) {
        <div class="check">
          <fa-icon [icon]="faMinus" />
        </div>
      }
    </div>
  `,
  styleUrls: ['./input-checkbox.component.scss'],
  imports: [FaIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputCheckboxComponent implements ControlValueAccessor, OnDestroy {
  private ngControl = inject(NgControl, { self: true, optional: true });

  readonly value = input(false);
  readonly readOnly = input(false);
  readonly positive = input(false);
  readonly disabled = input(false);
  readonly indeterminate = input(false);

  // allow component to update inner value, while still responding to input updates
  readonly controlValue = linkedSignal(() => this.value());
  readonly controlIndeterminate = linkedSignal(() => this.indeterminate());
  readonly controlDisabled = linkedSignal(() => this.disabled());

  readonly checked = computed(() => !!this.controlValue());

  // Output event
  readonly select = output<boolean>();

  constructor() {
    const ngControl = this.ngControl;

    // we need to set the valueAccessor here, so we can patch the value in the write without triggering updates,
    // but when the value is read it's still a boolean
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  ngOnDestroy() {
    // unset writeValue
    this.writeValue = () => {};
  }

  onClick($event: MouseEvent) {
    if (this.readOnly() || this.disabled()) {
      return;
    }
    $event.stopPropagation();
    // Toggle value and reset indeterminate
    this.controlValue.update((v) => !v);
    this.controlIndeterminate.set(false);
    this.select.emit(this.checked());
    this.onChangeCallback(this.checked());
  }

  // FORM CONTROL
  writeValue(inputValue: string | boolean | undefined): void {
    this.controlValue.set(!!inputValue);

    // if the controlValue does not match the inputValue, the input value was not a boolean primitive
    // in this case, we need to update the formControl with the new value, since the formControl internally has undefined stored.
    // we need to update the parent control value to a boolean, since the BE only accepts true/false for boolean fields
    if (this.controlValue() !== inputValue) {
      // the control might not exist, because of our order of operations
      // but will be called later again, and then the value will be set
      this.ngControl?.control?.patchValue(this.checked(), {
        emitEvent: false,
        onlySelf: true,
        emitModelToViewChange: false,
      });
    }
  }

  //call when something in the view changes
  onChangeCallback = (_: any) => {};

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  //not using registerOnTouched
  onTouchedCallback = (_: any) => {};

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.controlDisabled.set(isDisabled);
  }

  // END FORM CONTROL

  protected readonly faCheck = faCheck;
  protected readonly faMinus = faMinus;
}
